import { NavLink, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import InstallPrompt from "@/components/InstallPrompt";
import { Home, Dumbbell, Footprints, Utensils, BookOpen, LineChart, Users, MessageCircleQuestion, UserCircle2, Shield, LogOut, Moon, Flame, TrendingUp, Lock } from "lucide-react";

const BASE_NAV = [
  { to: "/app", label: "Dashboard", icon: Home, end: true },
  { to: "/app/lean", label: "LEAN", icon: Flame, requiresRebuild: true, pathway: "LEAN" },
  { to: "/app/build", label: "BUILD", icon: TrendingUp, requiresRebuild: true, pathway: "BUILD" },
  { to: "/app/train", label: "Train", icon: Dumbbell },
  { to: "/app/move", label: "Move", icon: Footprints },
  { to: "/app/eat", label: "Eat", icon: Utensils },
  { to: "/app/recover", label: "Recover", icon: Moon },
  { to: "/app/learn", label: "Learn", icon: BookOpen },
  { to: "/app/track", label: "Track", icon: LineChart },
  { to: "/app/community", label: "Community", icon: Users },
  { to: "/app/ask", label: "Ask BA30", icon: MessageCircleQuestion },
  { to: "/app/profile", label: "Profile", icon: UserCircle2 },
];

const GEN_COLOR = { BA30: "text-ba30", BA40: "text-ba40", BA50: "text-ba50", BA60: "text-ba60" };

export default function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/"); };
  const badgeColor = GEN_COLOR[user?.badge] || "text-primary";
  const [resetComplete, setResetComplete] = useState(false);
  const [activePathway, setActivePathway] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/journey").catch(() => ({ data: {} })),
      api.get("/pathway/state").catch(() => ({ data: {} })),
    ]).then(([j, p]) => {
      if (!alive) return;
      setResetComplete(!!j.data?.reset_complete);
      setActivePathway(p.data?.active_pathway || null);
    });
    return () => { alive = false; };
  }, []);

  // Hide LEAN/BUILD until 10K30 reset is complete — but always keep visible if that pathway is already active
  const NAV = BASE_NAV.filter((item) => {
    if (!item.requiresRebuild) return true;
    if (resetComplete) return true;
    if (activePathway === item.pathway) return true;
    return false;
  });

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <InstallPrompt />
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <div className="px-6 py-6">
          <Link to="/" className="wordmark text-2xl font-bold" data-testid="portal-brand">BA<span className="text-primary">30</span></Link>
          {user?.badge && (
            <div className={`mt-2 text-[10px] font-head uppercase tracking-[0.22em] ${badgeColor}`}>{user.badge} · {user.track}</div>
          )}
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-head uppercase tracking-wider transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-muted"}`}
              data-testid={`sidebar-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <Icon size={18} strokeWidth={2.25} />
              <span>{label}</span>
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/app/admin"
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-head uppercase tracking-wider transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-muted"}`}
              data-testid="sidebar-admin">
              <Shield size={18} strokeWidth={2.25} /> Admin
            </NavLink>
          )}
        </nav>
        <div className="border-t border-border p-4">
          <div className="text-sm font-semibold truncate">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <button onClick={doLogout} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-md border border-border py-2 text-xs font-head uppercase tracking-wider hover:bg-muted transition-colors" data-testid="logout-btn">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {/* Desktop top bar — persistent user + Log Out (mobile already has its own header below) */}
        <header className="hidden lg:flex sticky top-0 z-30 backdrop-blur-xl bg-background/85 border-b border-border">
          <div className="flex-1 flex items-center justify-between px-8 h-14">
            <div className="text-xs text-muted-foreground uppercase tracking-widest">
              {user?.name ? <>Signed in as <span className="text-foreground">{user.name}</span></> : "\u00A0"}
            </div>
            <button onClick={doLogout}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-head uppercase tracking-wider hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
              data-testid="logout-btn-top">
              <LogOut size={14} /> Log out
            </button>
          </div>
        </header>

        <header className="lg:hidden border-b border-border sticky top-0 z-40 backdrop-blur-xl bg-background/90">
          <div className="px-4 h-14 flex items-center justify-between">
            <Link to="/app" className="wordmark text-2xl font-bold">BA<span className="text-primary">30</span></Link>
            <button onClick={doLogout}
              className="inline-flex items-center gap-1.5 rounded-md bg-foreground text-background px-3 py-1.5 text-[11px] font-head uppercase tracking-wider hover:bg-primary hover:text-primary-foreground transition-colors"
              data-testid="logout-mobile-btn">
              <LogOut size={12} /> Log out
            </button>
          </div>
          <nav className="flex overflow-x-auto no-scrollbar border-t border-border px-2 py-2 gap-1">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end}
                className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-head uppercase tracking-wider whitespace-nowrap ${isActive ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/80"}`}>
                <Icon size={13} /> {label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="p-6 md:p-10 max-w-6xl">{children}</main>
      </div>
    </div>
  );
}
